// Approved GOMI-564 scope: Trust's Unicode ranges, the nine CSV symbols,
// and the complete half-width Japanese block. This is not an encoding whitelist.
const forbiddenCharacters = /['%\n\r"_,<>\uFF61-\uFF9F]/;
// GOMI-566 adds exactly these 59 characters without expanding the Unicode ranges.
const additionalForbiddenCharacters = new Set([
  ...'髙﨑𠮷德濵瀨栁桒橫淸靑黑內敎增寬頰剝吞噓姸瘦繫摑塡屛幷俱蟬蠟軀鷗禱顚簞溫麴醬萊朗隆塚晴猪益礼神祥福靖精羽諸逸都飯飼館鶴'
]);
const nonStandardRanges = [
  [0x2100, 0x214f],
  [0x2150, 0x218f],
  [0x2460, 0x24ff],
  [0x3200, 0x32ff],
  [0x3300, 0x33ff],
  [0xf900, 0xfaff],
  [0xe000, 0xf8ff],
  [0xf0000, 0xffffd],
  [0x100000, 0x10fffd]
] as const;

export const JWNET_ERROR_CLASS = 'jwnet-character-error';
export const JWNET_ERROR_MESSAGE =
  '環境依存文字、外字、半角カタカナ、禁則文字（「\'」シングルクォーテーション、「%」パーセント、「\\n」改行コード、「\\r」改行コード、「"」ダブルクォーテーション、「_」アンダースコア、「,」カンマ、「<」より大きい、「>」より小さい）は使用できません。';
type TextInput = HTMLInputElement | HTMLTextAreaElement;

export function hasInvalidJwnetCharacter(value: unknown): boolean {
  const text = String(value ?? '');
  return (
    forbiddenCharacters.test(text) ||
    [...text].some((character) => {
      if (additionalForbiddenCharacters.has(character)) return true;
      const codePoint = character.codePointAt(0)!;
      return nonStandardRanges.some(([start, end]) => codePoint >= start && codePoint <= end);
    })
  );
}

function isEditable(input: TextInput): boolean {
  return !input.disabled && !input.readOnly && !input.closest('[hidden]');
}

function isCheckedInput(input: TextInput): boolean {
  return (
    isEditable(input) &&
    !input.closest('[data-jwnet-exempt], .ant-select, .ant-picker, .ant-input-number') &&
    (input.tagName === 'TEXTAREA' || ['text', 'search', 'tel', 'url'].includes(input.type))
  );
}

export function clearJwnetErrors(root: HTMLElement): void {
  root.querySelectorAll(`.${JWNET_ERROR_CLASS}`).forEach((input) => {
    input.classList.remove(JWNET_ERROR_CLASS);
  });
}

export function validateJwnetInputs(root: HTMLElement): TextInput[] {
  clearJwnetErrors(root);
  const invalid = Array.from(root.querySelectorAll<TextInput>('input, textarea')).filter(
    (input) => {
      // Single-line HTML inputs strip LF/CR from .value. Keep the model value so
      // existing records containing those characters are still rejected on save.
      const value = input.getAttribute('data-jwnet-value') ?? input.value;
      return isCheckedInput(input) && hasInvalidJwnetCharacter(value);
    }
  );
  invalid.forEach((input) => input.classList.add(JWNET_ERROR_CLASS));
  return invalid;
}

export function clearCorrectedJwnetError(event: Event): void {
  const input = event.target as TextInput | null;
  if (input?.classList?.contains(JWNET_ERROR_CLASS) && !hasInvalidJwnetCharacter(input.value)) {
    input.classList.remove(JWNET_ERROR_CLASS);
  }
}

export function scrollToFirstFormError(root: HTMLElement): void {
  // Query all controls once to preserve visual DOM order across error types.
  const first = Array.from(root.querySelectorAll<TextInput>('input, textarea')).find((input) => {
    // Ant selectors/date pickers can use readonly inputs while remaining editable.
    const editableControl = input.closest(
      '.ant-select:not(.ant-select-disabled), .ant-picker:not(.ant-picker-disabled)'
    );
    const canScroll = isEditable(input) || (!!editableControl && !input.disabled);
    return (
      canScroll &&
      input.getClientRects().length > 0 &&
      (input.classList.contains(JWNET_ERROR_CLASS) ||
        input.closest(
          '.ant-form-item-has-error, .ant-input-status-error, .ant-picker-status-error'
        ))
    );
  });
  first?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  first?.focus({ preventScroll: true });
}
